/**
 * Exam taxonomies for the Australian Pharmacy Council (APC) registration exams.
 *
 * Domain codes, names and weightings are transcribed from APC's published exam
 * guides. They are the single source of truth for question generation, tagging,
 * seeding and every dashboard breakdown — change them here, nowhere else.
 *
 * Titrate is an independent product. It is not affiliated with, endorsed by
 * or connected to the Australian Pharmacy Council. CAOP and OPRA are trademarks
 * of the APC. No real exam question is ever reproduced.
 */
import { DIAGNOSTIC_QUESTION_COUNT } from './plans.js';
export const EXAM_CODES = ['CAOP', 'OPRA'];
/** What a user is preparing for. BOTH unlocks the taxonomies of each exam. */
export const EXAM_TARGETS = ['CAOP', 'OPRA', 'BOTH'];
export const QUESTION_TYPES = ['mcq', 'fib'];
export const DIFFICULTIES = ['easy', 'moderate', 'hard'];
export const TAG_TYPES = ['therapeutic_area', 'cognitive_level'];
/**
 * Therapeutic areas used as the primary tag dimension. APC publishes this as a
 * non-exhaustive list, so treat it as a starting tag set rather than a closed
 * enumeration — new areas can be added without a migration.
 */
export const THERAPEUTIC_AREAS = [
    'Analgesic',
    'Anti-infectives',
    'Cardiovascular',
    'Dermatology',
    'ENT',
    'Endocrinology',
    'Gastrointestinal',
    'Hepatic',
    'Haematology',
    'Immunology',
    'Nephrology',
    'Neurology',
    'Oncology',
    'Ophthalmology',
    'Pathophysiology',
    'Psychotropic',
    'Respiratory',
    'Rheumatology',
    'Toxicology',
    'Urology',
    'Vaccination',
    "Women's health",
];
/** Bloom/Anderson-derived cognitive levels, with OPRA's published sampling weights. */
export const COGNITIVE_LEVELS = [
    { type: 'cognitive_level', name: 'Remembering/Recall', weightPct: 55 },
    { type: 'cognitive_level', name: 'Comprehension/Understanding', weightPct: 30 },
    { type: 'cognitive_level', name: 'Application', weightPct: 15 },
];
const therapeuticAreaTags = THERAPEUTIC_AREAS.map((name) => ({
    type: 'therapeutic_area',
    name,
}));
export const CAOP = {
    code: 'CAOP',
    name: 'Competency Assessment of Overseas Pharmacists',
    description: 'Restricted open-book, computer-based assessment of practice competencies. 70 questions in 120 minutes, mixing four-option multiple choice with fill-in-the-blank pharmaceutical calculations.',
    questionCount: 70,
    timeLimitSec: 120 * 60,
    openBook: true,
    questionTypes: ['mcq', 'fib'],
    permittedReferences: [
        'Australian Medicines Handbook (AMH)',
        'Australian Pharmaceutical Formulary and Handbook (APF)',
        'British National Formulary (BNF)',
    ],
    domains: [
        {
            code: '3.1',
            name: 'Develop a patient-centred, culturally responsive approach to medication management',
            weightPct: 20,
            allowsCalculations: false,
        },
        { code: '3.2', name: 'Implement the medication management strategy or plan', weightPct: 30, allowsCalculations: true },
        { code: '3.3', name: 'Monitor and evaluate medication management', weightPct: 30, allowsCalculations: true },
        { code: '3.4', name: 'Compound medicines', weightPct: 10, allowsCalculations: true },
        { code: '3.6', name: 'Promote health and well-being', weightPct: 10, allowsCalculations: false },
    ],
    tags: therapeuticAreaTags,
};
export const OPRA = {
    code: 'OPRA',
    name: 'Overseas Pharmacist Readiness Assessment',
    description: 'Closed-book, computer-based knowledge assessment. 120 four-option multiple choice questions in 150 minutes, including dose calculations presented as multiple choice.',
    questionCount: 120,
    timeLimitSec: 150 * 60,
    openBook: false,
    questionTypes: ['mcq'],
    permittedReferences: [],
    domains: [
        { code: 'BIOMED', name: 'Biomedical sciences', weightPct: 20, allowsCalculations: false },
        { code: 'MEDCHEM', name: 'Medicinal chemistry and biopharmaceutics', weightPct: 10, allowsCalculations: true },
        { code: 'PKPD', name: 'Pharmacokinetics and pharmacodynamics', weightPct: 10, allowsCalculations: true },
        { code: 'PHARMTOX', name: 'Pharmacology and toxicology', weightPct: 15, allowsCalculations: false },
        { code: 'THERA', name: 'Therapeutics and patient care', weightPct: 45, allowsCalculations: true },
    ],
    tags: [...therapeuticAreaTags, ...COGNITIVE_LEVELS],
};
export const EXAM_SEEDS = [CAOP, OPRA];
export function getExamSeed(code) {
    const seed = EXAM_SEEDS.find((exam) => exam.code === code);
    if (!seed)
        throw new Error(`Unknown exam code: ${code}`);
    return seed;
}
/** Average seconds per question in the real exam — the pace users are benchmarked against. */
export function benchmarkPaceSec(code) {
    const seed = getExamSeed(code);
    return Math.round(seed.timeLimitSec / seed.questionCount);
}
/**
 * Time limit for the free diagnostic: the real paper's per-question pace held
 * across the shorter paper, so the pace score it produces is directly
 * comparable with a full-length sitting rather than being its own scale.
 */
export function diagnosticTimeLimitSec(code) {
    return benchmarkPaceSec(code) * DIAGNOSTIC_QUESTION_COUNT;
}
/** Difficulty bands the pool is bucketed by. */
export const POOL_DIFFICULTY_COUNT = 3;
/** How many of a paper's questions come from one content domain. */
export function questionsPerPaper(code, domainCode) {
    const seed = getExamSeed(code);
    const weightPct = seed.domains.find((d) => d.code === domainCode)?.weightPct ?? 0;
    return Math.round((weightPct / 100) * seed.questionCount);
}
/**
 * How deep the pool needs to be for one (domain x difficulty) bucket.
 *
 * A flat target per bucket cannot serve a weighted blueprint. OPRA draws 54 of
 * its 120 questions from THERA and 12 from MEDCHEM, so a single number is
 * either too small for the heavy domain — where candidates meet repeats first —
 * or wastefully large for the light one, and every unnecessary question is a
 * generation and a moderation call paid for. Sizing each bucket from the share
 * of the paper its domain actually carries is what makes "enough for N distinct
 * papers" mean the same thing in every domain.
 *
 * `floor` keeps a light domain from being provisioned so thinly that practice
 * sets filtered to it immediately repeat, which is a separate concern from
 * assembling a full paper.
 */
export function poolTargetForBucket(code, domainCode, opts) {
    const perPaper = questionsPerPaper(code, domainCode);
    const weighted = Math.ceil((opts.papers * perPaper) / POOL_DIFFICULTY_COUNT);
    return Math.max(opts.floor, weighted);
}
/** Short practice-set sizes offered in practice mode. */
export const PRACTICE_SET_SIZES = [10, 20, 40];
/**
 * Construction rules injected into every generation prompt. Mirrors APC's
 * published item-writing conventions so generated questions read like the real
 * thing without ever reproducing real content.
 */
export const QUESTION_STYLE_RULES = [
    'Use Australian TGA-approved drug names (generic names, Australian spelling).',
    'Use SI units throughout.',
    'Write determiners such as CORRECT, MOST, LEAST and NOT in bold and capitalised.',
    'Exactly one option is correct; distractors must be plausible to a candidate who has not mastered the concept.',
    'Never reproduce, paraphrase or reconstruct a real APC exam question. All content must be original.',
    'Do not reference or quote copyrighted reference texts verbatim.',
];
//# sourceMappingURL=exams.js.map